import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  subscribeRoom,
  confirmAnswer,
  handleAnswerTimeout,
  revealAnswers,
  showSolution,
  advanceToNext,
  startAnswering,
} from '../shared/roomTx';
import {
  FL_PHASE,
  FL_ROOM_STATUS,
  FL_SCRIPT_LABELS,
  FL_DIFFICULTY,
  getSlotLabel,
} from '../shared/fiveLeague';
import { estimateReadingDuration, testTTS, speak, stopSpeaking } from '../shared/tts';
import { isTTSEnabled, setTTSEnabled } from '../shared/ttsSettings';
import HandwritingCanvas from '../components/HandwritingCanvas';
import FeedbackModal from '../components/FeedbackModal';
import './Game.css';

// BGM管理
let bgmAudio = null;
function playBGM() {
  if (!bgmAudio) {
    bgmAudio = new Audio('/sounds/bgm.mp3');
    bgmAudio.loop = true;
    bgmAudio.volume = 0.1;
  }
  bgmAudio.play().catch(() => {});
}
function stopBGM() {
  if (bgmAudio) {
    bgmAudio.pause();
    bgmAudio.currentTime = 0;
  }
}

// 文字種判定ヘルパー
function getCharTypeLabel(char) {
  if (!char) return null;
  const c = char.trim();
  if (!c) return null;

  const smallHiragana = 'ぁぃぅぇぉっゃゅょゎ';
  const smallKatakana = 'ァィゥェォッャュョヮ';
  const isSmall = smallHiragana.includes(c) || smallKatakana.includes(c);

  if (/^[ぁ-ん]$/.test(c)) return isSmall ? 'ひ小' : 'ひ';
  if (/^[ァ-ヶー]$/.test(c)) return isSmall ? 'カ小' : 'カ';
  if (/^[一-龯々]$/.test(c)) return '漢';
  if (/^[0-9０-９]$/.test(c)) return '数';
  if (/^[A-Za-zＡ-Ｚａ-ｚ]$/.test(c)) return '英';

  return null;
}

// ═══════════════════════════════════════════════════════════
// GameDisplay: TV表示部分（16:9アスペクト）
// ═══════════════════════════════════════════════════════════
function GameDisplay({ room, game, phase, timeLeft, countdownNum, revealAnimation }) {
  const current = game?.current;
  const playerOrder = room?.playerOrder || [];
  const submissions = current?.submissions || [];
  const revealed = current?.revealed;
  const letters = revealed?.letters || [];
  const correctMask = revealed?.correctMask || [];
  const answerWord = revealed?.answerWord || current?.answer || [];
  const isPerfect = revealed?.isPerfect;

  return (
    <div className="game-display mini">
      <div className="display-content">
        {/* ヘッダー */}
        <div className="display-header">
          <div className="question-badge">
            <span className="q-num">第{(game?.questionIndex || 0) + 1}問</span>
            <span className="q-diff">{FL_DIFFICULTY[(game?.questionIndex || 0) + 1]?.label}</span>
          </div>
          <div className="category-badge">
            <span>{current?.category}</span>
            <span className="script-type">{FL_SCRIPT_LABELS[current?.script]}</span>
          </div>
        </div>

        {/* 問題文 */}
        <div className="display-prompt">
          <p>{current?.prompt}</p>
        </div>

        {/* 読み上げ中は問題文を強調表示（コントローラー側でメッセージ表示） */}

        {/* タイマー */}
        {phase === FL_PHASE.ANSWERING && (
          <div className={`display-timer ${timeLeft <= 10 ? 'danger' : ''}`}>
            {timeLeft}
          </div>
        )}

        {/* カウントダウン */}
        {phase === FL_PHASE.REVEAL_COUNTDOWN && (
          <div className="display-countdown" key={`countdown-${countdownNum}`}>
            <span>{countdownNum > 0 ? countdownNum : 'OPEN!'}</span>
          </div>
        )}

        {/* 文字スロット */}
        <div className="display-slots">
          {playerOrder.map((pid, idx) => {
            const player = room.players?.find(p => p.playerId === pid);
            const submission = submissions.find(s => s.index === idx);
            const isConfirmed = submission?.confirmedAt != null;

            // フェーズに応じて表示内容を変える
            let displayChar = '＿';
            if (phase === FL_PHASE.ANSWERING || phase === FL_PHASE.REVEAL_COUNTDOWN) {
              displayChar = isConfirmed ? '✓' : '＿';
            } else if (phase === FL_PHASE.REVEALED) {
              displayChar = revealAnimation ? (letters[idx] || '＿') : '？';
            } else if (phase === FL_PHASE.SOLUTION) {
              displayChar = answerWord[idx]?.trim() || letters[idx] || '＿';
            }

            // 正誤判定によるスタイル
            const isCorrect = correctMask[idx];
            let slotClass = 'display-slot';
            if ((phase === FL_PHASE.REVEALED && revealAnimation) || phase === FL_PHASE.SOLUTION) {
              slotClass += isCorrect ? ' correct' : ' incorrect';
              if (phase === FL_PHASE.REVEALED && revealAnimation) {
                slotClass += ' reveal-anim';
              }
            }

            // 文字種バッジ
            const charTypeLabel = (phase === FL_PHASE.REVEALED && revealAnimation) || phase === FL_PHASE.SOLUTION
              ? getCharTypeLabel(displayChar)
              : null;

            return (
              <div key={pid} className={slotClass} style={{ animationDelay: `${idx * 0.1}s` }}>
                <div className="slot-char">{displayChar}</div>
                {charTypeLabel && <div className="slot-type-badge">{charTypeLabel}</div>}
                <div className="slot-name">{player?.nickname || `P${idx + 1}`}</div>
              </div>
            );
          })}
        </div>

        {/* 正解表示 */}
        {phase === FL_PHASE.SOLUTION && (
          <div className="display-solution">
            <span className="solution-label">正解</span>
            <span className="solution-answer">
              {answerWord.map(c => c?.trim() || '').join('')}
            </span>
          </div>
        )}

        {/* スコア */}
        <div className="display-score">
          正解: {game?.correctQuestions || 0}問
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// GameController: 入力部分（スマホ用）
// ═══════════════════════════════════════════════════════════
function GameController({
  room,
  game,
  phase,
  playerId,
  roomId,
  mySlotIndex,
  isMyTurnConfirmed,
  mySubmission,
  confirmed,
  setConfirmed,
  onRecognize,
  candidates,
  allConfirmed,
  isEditing,
  setIsEditing,
  setCandidates,
  revealAnimation,
}) {
  const current = game?.current;
  const [showFeedback, setShowFeedback] = useState(false);

  const handleConfirm = async (char) => {
    if (!char) return;
    setConfirmed(true);
    setIsEditing(false);
    await confirmAnswer(roomId, playerId, char);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setConfirmed(false);
    setCandidates([]);
  };

  if (phase === FL_PHASE.READING) {
    // 問題読み上げ中
    return (
      <div className="controller-waiting reading-phase">
        <p className="reading-message">🎙️ 問題を読み上げ中...</p>
        <p className="reading-hint">よく聞いてね！</p>
      </div>
    );
  }

  if (phase === FL_PHASE.REVEAL_COUNTDOWN) {
    // カウントダウン中
    return (
      <div className="controller-waiting">
        <p className="countdown-message">回答オープンまで...</p>
      </div>
    );
  }

  if (phase === FL_PHASE.REVEALED || phase === FL_PHASE.SOLUTION) {
    // 結果表示（中央に大きく）- revealAnimation後に表示
    const revealed = game?.current?.revealed;
    const isPerfect = revealed?.isPerfect;
    const answerWord = revealed?.answerWord || game?.current?.answer || [];

    // TV画面と同期するため、revealAnimation が true になるまで待つ
    // SOLUTION フェーズでは即座に表示（REVEALED で既にアニメーション済み）
    if (phase === FL_PHASE.REVEALED && !revealAnimation) {
      return (
        <div className="controller-waiting">
          <p className="countdown-message">回答オープン！</p>
        </div>
      );
    }

    return (
      <div className="controller-result">
        <div className={`result-badge ${isPerfect ? 'perfect' : 'failed'}`}>
          {isPerfect ? '🎉 正解' : '😢 残念'}
        </div>
        <button
          className="feedback-btn"
          onClick={() => setShowFeedback(true)}
        >
          📝 この問題にフィードバック
        </button>
        <FeedbackModal
          isOpen={showFeedback}
          onClose={() => setShowFeedback(false)}
          questionId={game?.current?.questionId}
          questionPrompt={game?.current?.prompt}
        />
      </div>
    );
  }

  if (phase !== FL_PHASE.ANSWERING) {
    // その他のフェーズは待機表示
    return (
      <div className="controller-waiting">
        <p>しばらくお待ちください...</p>
      </div>
    );
  }

  // 確定済み & 編集中でない場合
  if (isMyTurnConfirmed && !isEditing) {
    return (
      <div className="controller-confirmed">
        <div className="confirmed-badge">✓ 確定済み</div>
        <p className="confirmed-char">{mySubmission?.chosen}</p>
        {!allConfirmed ? (
          <button className="edit-btn" onClick={handleEdit}>
            修正する
          </button>
        ) : (
          <p className="confirmed-waiting">全員確定！オープンを待っています...</p>
        )}
      </div>
    );
  }

  return (
    <div className="controller-input">
      <div className="my-slot-indicator">
        <span className="slot-label">あなたは</span>
        <span className="slot-position">{getSlotLabel(mySlotIndex)}</span>
        {isEditing && <span className="editing-badge">修正中</span>}
      </div>

      <HandwritingCanvas
        onRecognize={onRecognize}
        script={current?.script}
        disabled={false}
      />

      {candidates.length > 0 && (
        <div className="candidates-section">
          <p className="candidates-label">タップして確定</p>
          <div className="candidates-row">
            {candidates.map((c, i) => {
              const typeLabel = getCharTypeLabel(c);
              return (
                <button
                  key={i}
                  className={`candidate-btn ${i === 0 ? 'primary' : ''}`}
                  onClick={() => handleConfirm(c)}
                >
                  <span className="candidate-char">{c}</span>
                  {typeLabel && <span className="candidate-type">{typeLabel}</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// メインGame画面
// ═══════════════════════════════════════════════════════════
function Game({ navigate, playerId, roomId }) {
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeLeft, setTimeLeft] = useState(0);
  const [countdownNum, setCountdownNum] = useState(5);
  const [confirmed, setConfirmed] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [revealAnimation, setRevealAnimation] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // 音声設定（スマホはデフォルトOFF）
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const pref = isTTSEnabled();
    return pref !== null ? pref : false; // スマホはデフォルトOFF
  });
  const [ttsReady, setTtsReady] = useState(false);

  const phaseHandledRef = useRef({});
  const timerRef = useRef(null);

  // 音声トグル
  const toggleSound = async () => {
    const newEnabled = !soundEnabled;
    setSoundEnabled(newEnabled);
    setTTSEnabled(newEnabled);
    if (newEnabled && !ttsReady) {
      const ok = await testTTS();
      setTtsReady(ok);
    }
  };

  // TTS・BGM クリーンアップ
  useEffect(() => {
    // ゲーム開始時にBGM再生
    if (soundEnabled) {
      playBGM();
    }
    return () => {
      stopSpeaking();
      stopBGM();
    };
  }, []);

  // 音声設定変更時のBGM制御
  useEffect(() => {
    if (soundEnabled) {
      playBGM();
    } else {
      stopBGM();
    }
  }, [soundEnabled]);

  // TTS初期化（soundEnabledがtrueの場合）
  useEffect(() => {
    if (soundEnabled && !ttsReady) {
      testTTS().then(ok => setTtsReady(ok));
    }
  }, [soundEnabled, ttsReady]);

  // ルーム購読
  useEffect(() => {
    if (!roomId) {
      navigate('home');
      return;
    }

    const unsub = subscribeRoom(roomId, (data) => {
      setLoading(false);
      if (!data) {
        navigate('home');
        return;
      }

      // このプレイヤーがルームに参加しているか確認
      const isPlayer = data.players?.some(p => p.playerId === playerId);
      if (!isPlayer) {
        navigate('home');
        return;
      }

      setRoom(data);

      if (data.status === FL_ROOM_STATUS.FINISHED) {
        navigate('result', { roomId });
      }
    });

    return () => unsub();
  }, [roomId, playerId, navigate]);

  // フェーズ変更時の処理
  useEffect(() => {
    if (!room?.game) return;

    const game = room.game;
    const phase = game.phase;
    const questionIndex = game.questionIndex;
    const phaseKey = `${questionIndex}-${phase}`;

    // 新しい問題になったらリセット
    const prevKeys = Object.keys(phaseHandledRef.current);
    const currentQuestionPrefix = `${questionIndex}-`;
    if (prevKeys.some(k => !k.startsWith(currentQuestionPrefix))) {
      setConfirmed(false);
      setCandidates([]);
      setRevealAnimation(false);
      phaseHandledRef.current = {};
      clearInterval(timerRef.current);
    }

    // 既に処理済みならスキップ
    if (phaseHandledRef.current[phaseKey]) return;

    switch (phase) {
      case FL_PHASE.READING:
        phaseHandledRef.current[phaseKey] = true;
        // スマホでも音声ONなら読み上げ
        if (soundEnabled && ttsReady) {
          const current = game.current;
          const questionNumber = (game.questionIndex || 0) + 1;
          const difficulty = FL_DIFFICULTY[questionNumber];
          const introText = `第${questionNumber}問。${difficulty?.label || ''}。`;
          const categoryText = `カテゴリは、${current?.category}。`;
          // 括弧内を除去して読み上げ
          const promptText = (current?.prompt || '')
            .replace(/（[^）]*）/g, '')
            .replace(/\([^)]*\)/g, '')
            .trim();
          const fullText = `${introText}${categoryText}${promptText}`;
          speak(fullText);
        }
        // 読み上げ完了後に回答フェーズへ移行
        {
          const current = game.current;
          const questionNumber = (game.questionIndex || 0) + 1;
          // TTSの読み上げ時間を推定
          const waitMs = estimateReadingDuration(current, questionNumber);

          // ホストは通常タイミングで、他のプレイヤーは少し遅れてフォールバック
          const extraDelay = room.hostId === playerId ? 0 : 3000;
          const readingTimer = setTimeout(() => {
            // まだREADINGフェーズなら進める
            if (room?.game?.phase === FL_PHASE.READING) {
              startAnswering(roomId);
            }
          }, waitMs + extraDelay);
          return () => clearTimeout(readingTimer);
        }
        break;

      case FL_PHASE.ANSWERING:
        // 必ずタイマーをクリアしてから開始
        clearInterval(timerRef.current);
        phaseHandledRef.current[phaseKey] = true;

        // 回答時間をconfig から取得（デフォルト45秒）
        const answerTimeSec = room.config?.answerTimeSec || 45;
        // フェーズ開始時刻から計算
        const answerStartedAt = Date.now();
        const answerEndAt = answerStartedAt + answerTimeSec * 1000;

        // 初期値を設定
        setTimeLeft(answerTimeSec);

        const updateTimer = () => {
          const now = Date.now();
          const left = Math.max(0, Math.ceil((answerEndAt - now) / 1000));
          setTimeLeft(left);

          if (left <= 0) {
            clearInterval(timerRef.current);
            // タイムアウト処理（ホスト優先、2秒後にフォールバック）
            if (!phaseHandledRef.current[`${questionIndex}-timeout`]) {
              if (room.hostId === playerId) {
                phaseHandledRef.current[`${questionIndex}-timeout`] = true;
                handleAnswerTimeout(roomId);
              } else {
                // ホスト以外は2秒待ってからフォールバック
                setTimeout(() => {
                  if (!phaseHandledRef.current[`${questionIndex}-timeout`]) {
                    phaseHandledRef.current[`${questionIndex}-timeout`] = true;
                    handleAnswerTimeout(roomId);
                  }
                }, 2000);
              }
            }
          }
        };
        timerRef.current = setInterval(updateTimer, 200);
        return () => clearInterval(timerRef.current);

      case FL_PHASE.REVEAL_COUNTDOWN:
        phaseHandledRef.current[phaseKey] = true;
        clearInterval(timerRef.current);

        // サーバーのタイムスタンプを使ってカウントダウンを同期
        const revealOpenAt = game.current?.revealOpenAt || 0;
        // revealOpenAtが無効な場合はデフォルト5秒後に設定
        const actualRevealAt = revealOpenAt > Date.now() ? revealOpenAt : Date.now() + 5000;

        const updateCountdown = () => {
          const now = Date.now();
          const remaining = Math.max(0, Math.ceil((actualRevealAt - now) / 1000));
          setCountdownNum(remaining);

          if (remaining <= 0) {
            clearInterval(timerRef.current);
            // 結果を公開（ホスト優先、2秒後にフォールバック）
            if (!phaseHandledRef.current[`${questionIndex}-reveal`]) {
              if (room.hostId === playerId) {
                phaseHandledRef.current[`${questionIndex}-reveal`] = true;
                revealAnswers(roomId);
              } else {
                setTimeout(() => {
                  if (!phaseHandledRef.current[`${questionIndex}-reveal`] && game.phase === FL_PHASE.REVEAL_COUNTDOWN) {
                    phaseHandledRef.current[`${questionIndex}-reveal`] = true;
                    revealAnswers(roomId);
                  }
                }, 2000);
              }
            }
          }
        };
        updateCountdown(); // 即座に1回実行
        timerRef.current = setInterval(updateCountdown, 200); // 200msごとに更新
        return () => clearInterval(timerRef.current);

      case FL_PHASE.REVEALED:
        phaseHandledRef.current[phaseKey] = true;
        // 音声ONなら結果読み上げ
        if (soundEnabled && ttsReady) {
          const revealed = game.current?.revealed;
          const letters = revealed?.letters || [];
          const isPerfect = revealed?.isPerfect;
          const openText = '回答オープン！';
          const lettersText = letters.join('、');
          const resultText = isPerfect ? '正解！' : '残念、不正解';
          speak(`${openText}${lettersText}。${resultText}`);
        }
        // TV画面のTTS「回答オープン！」読み上げ後に同期（約2.5秒）
        setTimeout(() => {
          setRevealAnimation(true);
        }, 2500);
        {
          const holdTime = (room.config?.revealHoldSec || 10) * 1000;
          const extraDelay = room.hostId === playerId ? 0 : 3000;
          const revealTimer = setTimeout(() => {
            if (room?.game?.phase === FL_PHASE.REVEALED) {
              showSolution(roomId);
            }
          }, holdTime + extraDelay);
          return () => clearTimeout(revealTimer);
        }

      case FL_PHASE.SOLUTION:
        phaseHandledRef.current[phaseKey] = true;
        {
          const holdTime = (room.config?.solutionHoldSec || 3) * 1000;
          const extraDelay = room.hostId === playerId ? 0 : 3000;
          const solutionTimer = setTimeout(() => {
            if (room?.game?.phase === FL_PHASE.SOLUTION) {
              advanceToNext(roomId);
            }
          }, holdTime + extraDelay);
          return () => clearTimeout(solutionTimer);
        }
    }

    return () => {
      clearInterval(timerRef.current);
    };
  }, [room?.game?.phase, room?.game?.questionIndex, roomId, playerId, room?.hostId, room?.config, soundEnabled, ttsReady]);

  const handleRecognize = useCallback((results) => {
    setCandidates(results.slice(0, 5));
  }, []);

  if (loading || !room?.game) {
    return (
      <div className="game-container">
        <div className="loading">読み込み中...</div>
      </div>
    );
  }

  const game = room.game;
  const phase = game.phase;
  const me = room.players?.find(p => p.playerId === playerId);
  const mySlotIndex = me?.slotIndex ?? -1;
  const submissions = game.current?.submissions || [];
  const mySubmission = submissions.find(s => s.playerId === playerId);
  const isMyTurnConfirmed = mySubmission?.confirmedAt != null;
  const allConfirmed = submissions.length > 0 && submissions.every(s => s.confirmedAt != null);

  return (
    <div className="game-container controller-mode">
      {/* 音声トグルボタン */}
      <button
        className={`game-sound-toggle ${soundEnabled ? 'on' : 'off'}`}
        onClick={toggleSound}
        aria-label={soundEnabled ? '音声OFF' : '音声ON'}
      >
        {soundEnabled ? '🔊' : '🔇'}
      </button>

      {/* 上部: ミニTV表示 */}
      <GameDisplay
        room={room}
        game={game}
        phase={phase}
        timeLeft={timeLeft}
        countdownNum={countdownNum}
        revealAnimation={revealAnimation}
      />

      {/* 下部: 入力コントローラー */}
      <GameController
        room={room}
        game={game}
        phase={phase}
        playerId={playerId}
        roomId={roomId}
        mySlotIndex={mySlotIndex}
        isMyTurnConfirmed={isMyTurnConfirmed}
        mySubmission={mySubmission}
        confirmed={confirmed}
        setConfirmed={setConfirmed}
        onRecognize={handleRecognize}
        candidates={candidates}
        allConfirmed={allConfirmed}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        setCandidates={setCandidates}
        revealAnimation={revealAnimation}
      />

      {/* 確定状況バー */}
      {phase === FL_PHASE.ANSWERING && (
        <div className="confirm-status-bar">
          確定: {submissions.filter(s => s.confirmedAt).length} / {submissions.length}
        </div>
      )}
    </div>
  );
}

export default Game;
